import { useContext, useEffect, useMemo, useRef } from 'react'

import { trackSearchCalendarEventOverlayImpression } from 'analytics/snowplow/events/searchCalendarEventOverlayEvents'
import type { CalendarVisibleMonths } from 'components/Common/Calendar/calendarFocusMonth'
import { Experiments } from 'constants/experiments'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import useExperiment from 'hooks/Experiments/useExperiment'
import useSearchCalendarEvents from 'hooks/Search/useSearchCalendarEvents'
import {
  countHighlightedDatesInMonth,
  eventForMonthKey,
  eventsExcludingPublicHolidayMarkers,
  eventsForPlaceIds,
  eventsIntersectingMonths,
  eventsNearMonths,
  offsetMonthKey,
  selectPrimaryEvents,
  selectPublicHolidays,
} from 'lib/search/searchCalendarEvents'
import { getPlaceIdsFromSearchItem } from 'lib/search/searchedPlaces'

function useSearchCalendarEventOverlay(visibleMonths?: CalendarVisibleMonths, isOpen = false) {
  const { searchItem } = useContext(GlobalSearchStateContext)
  const impressedMonthsRef = useRef(new Set<string>())

  /**
   * Keyed on `isOpen` only — never on the experiment variation. Both arms have
   * to fetch at the same moment under the same conditions, or treatment's
   * eligible population would differ from control's and bias the apply ratio.
   */
  const events = useSearchCalendarEvents(isOpen)

  const placeIds = useMemo(
    () => searchItem ? getPlaceIdsFromSearchItem(searchItem) : [],
    [searchItem],
  )
  const matchingRows = useMemo(
    () => eventsForPlaceIds(events, placeIds),
    [events, placeIds],
  )
  const matchingEvents = useMemo(
    () => eventsExcludingPublicHolidayMarkers(matchingRows),
    [matchingRows],
  )
  /** Exposure population — one month either side of an event month still counts. */
  const canActivate = useMemo(
    () => eventsNearMonths(matchingEvents, visibleMonths).length > 0,
    [matchingEvents, visibleMonths],
  )
  /** Non-overlapping events whose dates land in the visible 2-month block. */
  const primaryEvents = useMemo(
    () => selectPrimaryEvents(eventsIntersectingMonths(matchingEvents, visibleMonths)),
    [matchingEvents, visibleMonths],
  )
  /** Public holidays from the same feed — pip/bar markers, never the range wash. */
  const publicHolidays = useMemo(
    () => selectPublicHolidays(eventsIntersectingMonths(matchingRows, visibleMonths)),
    [matchingRows, visibleMonths],
  )

  const { isAnyVariant, isTurnedOn } = useExperiment(Experiments.searchCalendarEventOverlay, { canActivate })
  const showOverlay = primaryEvents.length > 0 && isAnyVariant

  /**
   * Events in the months paging would newly reveal — one step either side. Keyed on
   * the month that becomes visible, not the whole adjacent viewport, so a hint never
   * points at an event already painted on screen.
   */
  const adjacentEvents = useMemo(() => {
    if (!visibleMonths?.left) return {}

    const previousMonthKey = offsetMonthKey(visibleMonths.left, -1)
    const nextMonthKey = offsetMonthKey(visibleMonths.right ?? visibleMonths.left, 1)

    return {
      previousMonthEvent: eventForMonthKey(matchingEvents, previousMonthKey),
      previousMonthKey,
      nextMonthEvent: eventForMonthKey(matchingEvents, nextMonthKey),
      nextMonthKey,
    }
  }, [matchingEvents, visibleMonths])

  /**
   * How a suggestion surfaced, kept apart because they are different strength of
   * evidence — see `maybeTrackSearchCalendarEventOverlayApply`.
   *
   * Both are computed for either arm. Control paints and hints nothing, but it needs
   * the same denominators, otherwise treatment's larger eligible population would
   * bias the apply ratio between them.
   */
  const hadVisibleSuggestion = primaryEvents.length > 0
  const hadHintedSuggestion = !hadVisibleSuggestion && (
    !!adjacentEvents.previousMonthEvent || !!adjacentEvents.nextMonthEvent
  )

  const visibleMonthKeys = useMemo(
    () => [visibleMonths?.left, visibleMonths?.right]
      .filter((monthKey): monthKey is string => !!monthKey),
    [visibleMonths],
  )

  useEffect(() => {
    if (!isOpen) {
      impressedMonthsRef.current.clear()
      return
    }

    if (!isTurnedOn || !primaryEvents.length) return

    for (const event of primaryEvents) {
      for (const monthKey of visibleMonthKeys) {
        if (countHighlightedDatesInMonth(event, monthKey) === 0) continue

        const impressionKey = `${monthKey}:${event.id}`
        if (impressedMonthsRef.current.has(impressionKey)) continue

        impressedMonthsRef.current.add(impressionKey)
        trackSearchCalendarEventOverlayImpression({
          monthKey,
          event,
          placeIds,
          isHighlighted: isAnyVariant,
        })
      }
    }
  }, [isAnyVariant, isOpen, isTurnedOn, placeIds, primaryEvents, visibleMonthKeys])

  return useMemo(() => ({
    canActivate,
    /** Treatment variation — gates anything the overlay draws, hints included. */
    isAnyVariant,
    isTurnedOn,
    matchingEvents,
    placeIds,
    hadHintedSuggestion,
    hadVisibleSuggestion,
    primaryEvents,
    publicHolidays,
    showOverlay,
    ...adjacentEvents,
  }), [
    adjacentEvents,
    canActivate,
    isAnyVariant,
    hadHintedSuggestion,
    hadVisibleSuggestion,
    isTurnedOn,
    matchingEvents,
    placeIds,
    primaryEvents,
    publicHolidays,
    showOverlay,
  ])
}

export default useSearchCalendarEventOverlay
